/**
 * 通用资源（数字资产）前后端字段适配层。
 *
 * 业务语义 → 后端契约：
 * - resourceName ↔ wallpaperName
 * - assetType ↔ wallpaperType（表字段 wallpaper_type）
 *
 * 前端模块内统一使用 resourceName / assetType，仅在本文件与 API 边界做映射。
 *
 * 勿与 resourceType（整数，如 21 = CMS 资源大类）混淆：assetType 是单条资源的字符串子类型。
 */
import { toExportWallpaperType, toTypeQuery } from "./resourceTypeFilter";

/**
 * 将 API 行/详情 VO 归一化为前端领域字段，保留原契约字段以兼容未改动的调用方。
 */
export function normalizeGenericResourceRow(row) {
  if (!row || typeof row !== "object") {
    return row;
  }
  const resourceName =
    row.resourceName != null && row.resourceName !== ""
      ? row.resourceName
      : row.wallpaperName || "";
  const assetType =
    row.assetType != null && row.assetType !== ""
      ? row.assetType
      : row.wallpaperType || "";
  return {
    ...row,
    resourceName,
    assetType,
  };
}

/** 批量归一化列表数据。 */
export function normalizeGenericResourceList(rows) {
  return (rows || []).map(normalizeGenericResourceRow);
}

/** 详情页与编辑页复用同一归一化逻辑。 */
export const normalizeGenericResourceDetail = normalizeGenericResourceRow;

/**
 * 表单/批量上传 → 保存契约 WallpaperCo（JSON 内仍用 wallpaperName / wallpaperType）。
 * 剥离前端领域字段，避免把 resourceName / assetType 当作未知字段传给后端。
 */
export function toWallpaperCo(form) {
  if (!form || typeof form !== "object") {
    return form;
  }
  const { resourceName, assetType, ...rest } = form;
  return {
    ...rest,
    wallpaperName:
      resourceName != null && resourceName !== ""
        ? resourceName
        : rest.wallpaperName || "",
    wallpaperType:
      assetType != null && assetType !== ""
        ? assetType
        : rest.wallpaperType || "",
  };
}

/**
 * 列表查询：前端筛选项 → GET /genericResource/list 查询参数。
 * assetType 是资源类型两栏筛选的选中值，可能是某组的 All、组内几项或贴纸子类型，见 resourceTypeFilter。
 */
export function toLinkListQueryParams(params) {
  const {
    pageNum,
    pageSize,
    resourceName,
    assetType,
    wallpaperStatus,
    categoryId,
    key,
    dataSetId,
  } = params || {};

  const query = {};
  if (pageNum != null) {
    query.pageNum = pageNum;
  }
  if (pageSize != null) {
    query.pageSize = pageSize;
  }
  if (resourceName) {
    query.wallpaperName = resourceName;
  }
  if (assetType) {
    Object.assign(query, toTypeQuery(assetType));
  }
  if (
    wallpaperStatus !== "" &&
    wallpaperStatus != null &&
    wallpaperStatus !== undefined
  ) {
    query.wallpaperStatus = wallpaperStatus;
  }
  if (categoryId !== "" && categoryId != null && categoryId !== undefined) {
    query.categoryId = categoryId;
  }
  if (key) {
    query.key = key;
  }
  if (dataSetId !== "" && dataSetId != null && dataSetId !== undefined) {
    query.dataSetId = dataSetId;
  }
  return query;
}

/**
 * 导出 / 打标模板：前端 filters → WallpaperExportCmd 契约字段。
 */
export function toExportQueryPayload(resourceType, filters, keySet) {
  const payload = {
    resourceType,
    wallpaperName: filters.resourceName || "",
    // 导出接口只认单个类型：整组导出不按类型筛，贴纸子类型退回全部贴纸
    wallpaperType: toExportWallpaperType(filters.assetType),
    categoryId: filters.categoryType || undefined,
    key: filters.key || "",
    dataSetId: filters.dataSetId || undefined,
  };
  if (
    filters.wallpaperStatus !== "" &&
    filters.wallpaperStatus != null &&
    filters.wallpaperStatus !== undefined
  ) {
    payload.wallpaperStatus = filters.wallpaperStatus;
  }
  if (keySet && keySet.length) {
    payload.keySet = keySet;
  }
  return payload;
}
